import { Link } from 'react-router-dom';
import { Monitor, Smartphone, Tablet, Undo2, Redo2, Save, Globe, ChevronLeft } from 'lucide-react';
import { cn } from '../../ui/cn.ts';
import { Button } from '../../ui/Button.tsx';

export type DeviceWidth = 375 | 768 | null;

interface Props {
  spaceId: string;
  pageTitle: string;
  pageStatus: 'draft' | 'published';
  isDirty: boolean;
  isSaving: boolean;
  isPublishing: boolean;
  canUndo: boolean;
  canRedo: boolean;
  deviceWidth: DeviceWidth;
  onDeviceChange: (w: DeviceWidth) => void;
  onUndo: () => void;
  onRedo: () => void;
  onSave: () => void;
  onPublish: () => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DEVICES: { label: string; width: DeviceWidth; Icon: React.ComponentType<any> }[] = [
  { label: 'Desktop', width: null, Icon: Monitor },
  { label: 'Tablet', width: 768, Icon: Tablet },
  { label: 'Mobile', width: 375, Icon: Smartphone },
];

export function BuilderToolbar({
  spaceId,
  pageTitle,
  pageStatus,
  isDirty,
  isSaving,
  isPublishing,
  canUndo,
  canRedo,
  deviceWidth,
  onDeviceChange,
  onUndo,
  onRedo,
  onSave,
  onPublish,
}: Props) {
  return (
    <div className="flex h-12 shrink-0 items-center justify-between border-b border-gray-200 bg-white px-3">
      {/* Left: back + page title */}
      <div className="flex items-center gap-3">
        <Link
          to={`/spaces/${spaceId}/pages`}
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900 transition-colors"
        >
          <ChevronLeft size={14} />
          Pages
        </Link>
        <span className="text-gray-300">|</span>
        <span className="text-sm font-medium text-gray-900 max-w-48 truncate">{pageTitle}</span>
        <span
          className={cn(
            'rounded px-1.5 py-0.5 text-[10px] font-medium',
            pageStatus === 'published'
              ? 'bg-emerald-50 text-emerald-600'
              : 'bg-gray-100 text-gray-600',
          )}
        >
          {pageStatus.toUpperCase()}
        </span>
      </div>

      {/* Center: device switcher */}
      <div className="flex items-center gap-0.5 rounded-md bg-gray-100 p-0.5">
        {DEVICES.map(({ label, width, Icon }) => (
          <button
            key={label}
            title={label}
            onClick={() => onDeviceChange(width)}
            className={cn(
              'flex items-center gap-1 rounded px-2.5 py-1 text-xs transition-colors',
              deviceWidth === width
                ? 'bg-zinc-900 text-white'
                : 'text-gray-500 hover:bg-gray-200 hover:text-gray-900',
            )}
          >
            <Icon size={13} />
          </button>
        ))}
      </div>

      {/* Right: undo/redo + save + publish */}
      <div className="flex items-center gap-1.5">
        <button
          title="Undo (Ctrl+Z)"
          disabled={!canUndo}
          onClick={onUndo}
          className="flex items-center rounded p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-30"
        >
          <Undo2 size={14} />
        </button>
        <button
          title="Redo (Ctrl+Shift+Z)"
          disabled={!canRedo}
          onClick={onRedo}
          className="flex items-center rounded p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 disabled:cursor-not-allowed disabled:opacity-30"
        >
          <Redo2 size={14} />
        </button>
        <div className="mx-1 h-5 w-px bg-gray-200" />
        <Button
          size="sm"
          variant="secondary"
          disabled={!isDirty || isSaving}
          onClick={onSave}
          className="bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300 disabled:opacity-40"
        >
          <Save size={12} className="mr-1.5" />
          {isSaving ? 'Saving…' : 'Save'}
        </Button>
        <Button
          size="sm"
          disabled={isPublishing}
          onClick={onPublish}
          className="bg-emerald-600 hover:bg-emerald-500 border-emerald-600 text-white"
        >
          <Globe size={12} className="mr-1.5" />
          {isPublishing ? 'Publishing…' : 'Publish'}
        </Button>
      </div>
    </div>
  );
}
